import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Target, Brain, TrendingUp } from "lucide-react";
import { getAssessmentGraph } from "../services/dashboardService";
import { 
  setAssessmentData, 
  setAssessmentLoading, 
  setAssessmentError,
  selectAssessmentData,
  selectAssessmentLoading,
  selectDependenciesChanged
} from "../redux/assessmentSlice";
import { setHasAIJobImpact } from "../redux/userSlice";
import { updateHasAIJobImpact } from "../services/profileService";
import Loader from "../components/loader/Loader";
import Sidebar from "../components/Sidebar";
import SkillsetCard from "../components/skillsetCard/SkillsetCard";

const AiJobImpactAssesment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentJob, occupationId, currentJobDescription, hasAIJobImpact } = useSelector((state) => state.user);
  const { id: userId } = useSelector((state) => state.auth);
  
  const assessmentData = useSelector(selectAssessmentData);
  const isLoading = useSelector(selectAssessmentLoading);
  const dependenciesChanged = useSelector(selectDependenciesChanged);

  const selectedCareer = location.state?.selectedCareer;
  const displayJob = selectedCareer || currentJob || "AI Prompt Engineer";
  const aiImpactScore = assessmentData?.ai_job_impact_score ?? 45;

  useEffect(() => {
    if ( occupationId && (selectedCareer || currentJob) && currentJobDescription) {
      if (!assessmentData || dependenciesChanged) {
        (async () => {
          try {
            dispatch(setAssessmentLoading(true));
            const data = await getAssessmentGraph(occupationId, selectedCareer || currentJob, currentJobDescription);
            
            dispatch(setAssessmentData({
              data,
              dependencies: {
                occupationId,
                currentJob: selectedCareer || currentJob,
                currentJobDescription,
                selectedCareer,
              }
            }));

            if (!hasAIJobImpact) {
              await updateHasAIJobImpact(userId, true);
              dispatch(setHasAIJobImpact(true));
            }
          } catch (err) {
            console.error("Error fetching assessment data:", err);
            dispatch(setAssessmentError("Failed to fetch assessment data"));
          }
        })();
      }
    }
  }, [ occupationId, currentJob, currentJobDescription, selectedCareer, assessmentData, dependenciesChanged, dispatch]);


  const getColor = score => score <= 33 ? "#10b981" : score <= 66 ? "#f59e0b" : "#ef4444";
  
  const getAutomationRange = (score) => {
    if (score >= 0 && score <= 20) return "Very Low";
    if (score >= 21 && score <= 40) return "Low";
    if (score >= 41 && score <= 60) return "Medium";
    if (score >= 61 && score <= 80) return "High";
    if (score >= 81 && score <= 100) return "Critical";
    return "Unknown";
  };

  if (isLoading) return <Loader />;

  return (
    <Sidebar>
      <div className="max-w-6xl mx-auto p-3 sm:p-4 lg:p-6 min-h-screen">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-800 mb-2 sm:mb-3">AI Job Impact Assessment</h1>
          <p className="text-sm sm:text-base text-gray-600 px-2">This is how AI is transforming your job as a <span className="font-semibold text-gray-800">"{displayJob}"</span></p>
        </div>

        <div className="rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-300 mb-6 sm:mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 sm:gap-8 lg:gap-12">
            <div className="lg:col-span-4 flex flex-col items-center">
              <div className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 mb-4 sm:mb-6">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                  <circle
                    cx="50" cy="50" r="40" fill="none"
                    stroke={getColor(aiImpactScore)} strokeWidth="8"
                    strokeDasharray={`${aiImpactScore * 2.51}, 251`} strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ color: getColor(aiImpactScore) }}>{aiImpactScore}%</span>
                </div>
              </div>
              <button onClick={() => navigate("/dashboard/task-automation-analysis")} className="border border-blue-500 text-blue-500 px-4 sm:px-6 lg:px-8 py-2 sm:py-3 rounded-full hover:bg-blue-50 transition-all duration-200 hover:shadow-lg font-medium text-sm sm:text-base lg:text-lg">
                View Analysis
              </button>
            </div>

            <div className="lg:col-span-6">
              <h2 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">Estimated Automation Potential</h2>
              <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 leading-relaxed">
                Based on comprehensive analysis of various factors including task complexity, technological readiness, economic viability, and human interaction requirements.
              </p>
              <div className="mb-4 sm:mb-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-black text-sm sm:text-base">Automation Potential</p>
                    <p className="text-black text-xs sm:text-sm">Likelihood of routine and repetitive tasks being automated by AI systems</p>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 bg-gray-50 rounded-lg p-3 sm:p-4">
                <p className="font-semibold text-gray-500 mb-3 text-sm sm:text-base">Automation Spectrum</p>
                <div className="relative mb-4">
                  <div className="h-2 sm:h-3 bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 rounded-full mb-2"></div>
                  <div className="absolute top-0 w-3 h-3 sm:w-4 sm:h-4 rounded-full transform -translate-x-1/2 shadow-lg border-2 border-orange-500 bg-white" style={{ left: `${aiImpactScore}%` }}></div>
                </div>
                
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className={getAutomationRange(aiImpactScore) === "Very Low" ? "text-black font-bold" : "text-gray-600"}>Very Low</span>
                  <span className={getAutomationRange(aiImpactScore) === "Low" ? "text-black font-bold" : "text-gray-600"}>Low</span>
                  <span className={getAutomationRange(aiImpactScore) === "Medium" ? "text-black font-bold" : "text-gray-600"}>Medium</span>
                  <span className={getAutomationRange(aiImpactScore) === "High" ? "text-black font-bold" : "text-gray-600"}>High</span>
                  <span className={getAutomationRange(aiImpactScore) === "Critical" ? "text-black font-bold" : "text-gray-600"}>Critical</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 sm:mb-8">
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-600 rounded-full flex items-center justify-center">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-blue-900">Impact on the Job</h3>
            </div>
            <div className="text-sm sm:text-base text-gray-600 leading-relaxed">
              {assessmentData?.impact_on_the_role ? <p>{assessmentData.impact_on_the_role}</p> : <p>No content</p>}
            </div>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
          <SkillsetCard
            title="Skills Evolution"
            data={assessmentData?.skill_evolution || []}
            icon={Brain}
            count={assessmentData?.skill_evolution?.length || 0}
          />
          
          <SkillsetCard
            title="Future Projections"
            data={assessmentData?.future_projections || []}
            icon={TrendingUp}
            count={assessmentData?.future_projections?.length || 0}
          />
        </div>

        <div className="text-center mt-6 sm:mt-8">
          <button onClick={() => navigate("/dashboard/career-discovery/career-recommendations")} className="border border-gray-300 text-purple-600 py-2 sm:py-3 px-4 sm:px-7 rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-lg font-semibold text-sm sm:text-base lg:text-lg mb-2 sm:mb-3">
            View AI Augmented Careers for this Job
          </button>
          <p className="text-xs sm:text-sm text-gray-600 px-2">Emerging AI Careers, Augmented AI Careers related to this occupation</p>
        </div>
      </div>
    </Sidebar>
  );
};

export default AiJobImpactAssesment;
